import {
  createColumnHelper,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { useSessionStorage } from "@uidotdev/usehooks";
import Table from "../../../ui/Table/Table";
import styles from "./SearchByURL.module.scss";
import { Asterisk, Search } from "../../../ui/Icon/Icon";
import TextInput from "../../../ui/TextInput/TextInput";
import { useCallback, useEffect, useState } from "react";
import {
  Intent,
  KeywordsByUrlFilter,
  KeywordSort,
  KeywordSortField,
  SortOrder,
  Trend,
} from "../../../__generated__/gql/graphql";
import { graphql } from "../../../__generated__/gql";
import { useQuery } from "urql";
import DifficultyCell from "../DifficultyCell/DifficultyCell";
import IntentCell from "../IntentCell/IntentCell";
import TrendCell from "../TrendCell/TrendCell";
import KeywordCell from "../KeywordCell/KeywordCell";
import EmptyState from "../../../ui/EmptyState/EmptyState";
import KeywordTableToolbar from "../KeywordTableToolbar/KeywordTableToolbar";
import KeywordFilterButton, {
  KeywordFilterState,
} from "../KeywordFilterButton/KeywordFilterButton";
import Truncate from "../../../ui/Truncate/Truncate";
import getUrl from "../../../util/get-url";
import CountryLanguageSelect from "../CountryLanguageSelect/CountryLanguageSelect";
import useUser from "../../../providers/UserProvider/useUser";
import Feedback from "../../../ui/Feedback/Feedback";
import validateUrl from "../../../util/validate-url";
import { useDisclosure } from "../../../ui/util/useDisclosure";
import InsufficientKeywordCreditsModal from "../InsufficientKeywordCreditsModal/InsufficientKeywordCreditsModal";
import Suggestions from "../Suggestions/Suggestions";

const SearchByUrlSettingsQuery = graphql(/* GraphQL */ `
  query searchByUrlSettings {
    keywordSettings {
      defaultGoogleLocation
    }
  }
`);

const SearchByURLQuery = graphql(/* GraphQL */ `
  query keywordsByURL(
    $filter: KeywordsByUrlFilter!
    $sort: KeywordSort
    $paginationOptions: PagePaginationOptions
  ) {
    keywordUrlSuggestions
    keywordsByUrl(
      filter: $filter
      sort: $sort
      paginationOptions: $paginationOptions
    ) {
      totalCount
      nodes {
        keyword
        rank
        url
        difficulty
        searchVolume
        intent
        trend {
          date
          value
        }
      }
    }
  }
`);

type Result = {
  id: string;
  keyword: string;
  rank: number;
  url: string;
  difficulty?: number | null;
  searchVolume?: number | null;
  intent?: Intent | null;
  trend?: Trend[] | null;
};

const columnHelper = createColumnHelper<Result>();

const columns = [
  columnHelper.accessor("keyword", {
    header: "Keyword",
    cell: KeywordCell,
    enableSorting: false,
    minSize: 200,
    meta: {
      tooltip: "The search term used on Google",
      sticky: true,
    },
  }),
  columnHelper.accessor("url", {
    header: "URL",
    enableSorting: false,
    size: 360,
    cell: ({ cell }) => <Truncate>{cell.getValue()}</Truncate>,
    meta: {
      tooltip: "The primary URL that is ranking for this keyword",
    },
  }),
  columnHelper.accessor("rank", {
    header: "Rank",
    size: 105,
    enableSorting: false,
    meta: {
      tooltip:
        "The ranking position that this URL appears in Google for the keyword. 1st position is the top",
    },
  }),
  columnHelper.accessor("difficulty", {
    header: "Difficulty",
    size: 130,
    cell: DifficultyCell,
    meta: {
      tooltip:
        "How difficult it is to rank for this keyword, from easy to hard. Anything below a 30 will be relatively easy, anything above a 70 will be very difficult",
    },
  }),
  columnHelper.accessor("searchVolume", {
    header: "Volume",
    size: 120,
    cell: ({ cell }) => {
      const value = cell.getValue();

      return value ? Intl.NumberFormat().format(value) : "-";
    },
    meta: {
      tooltip: "The estimated number of searches for this keyword each month",
    },
  }),
  columnHelper.accessor("intent", {
    header: "Intent",
    enableSorting: false,
    size: 168,
    cell: IntentCell,
    meta: {
      tooltip: "The search intent for this keyword",
    },
  }),
  columnHelper.accessor("trend", {
    header: "Trend",
    enableSorting: false,
    size: 120,
    cell: TrendCell,
    meta: {
      tooltip: "The history of the search volume over the past 12 months",
    },
  }),
];

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 50,
};

function SearchByURL() {
  const { tenant, keywordQueryCreditsRemaining } = useUser();
  const [
    insufficientKeywordCreditsModalOpened,
    insufficientKeywordCreditsModal,
  ] = useDisclosure();

  const [searchByUrlSettingsResponse] = useQuery({
    query: SearchByUrlSettingsQuery,
  });

  const [url, setUrl] = useSessionStorage<string>(
    `${tenant?.id}:searchByURLUrl`,
    "",
  );
  const [debouncedUrl, setDebouncedUrl] = useState<string>(
    validateUrl(url) ? url : "",
  );
  const [urlError, setUrlError] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedCountry, setSelectedCountry] = useState("us:en");
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [sort, setSort] = useState<KeywordSort>();
  const [debouncedSort, setDebouncedSort] = useState<KeywordSort>();
  const [filters, setFilters] = useState<KeywordFilterState>({
    searchIntent: null,
    searchVolume: null,
    keywordDifficulty: null,
    excludeKeywords: null,
    includeKeywords: null,
    rank: null,
  });

  const filterCount =
    (filters.searchVolume ? 1 : 0) +
    (filters.keywordDifficulty ? 1 : 0) +
    (filters.searchIntent ? 1 : 0) +
    (filters.includeKeywords ? 1 : 0) +
    (filters.excludeKeywords ? 1 : 0) +
    (filters.rank ? 1 : 0);

  const onSortingChange = useCallback((sortState: SortingState) => {
    const sort = sortState[0];

    if (!sort) {
      setSort(undefined);
      return;
    }

    const field = sort.id;
    const direction = sort.desc ? SortOrder.Desc : SortOrder.Asc;

    if (Object.values(KeywordSortField).includes(field as KeywordSortField)) {
      setSort({
        field: field as KeywordSortField,
        direction,
      });
    } else {
      setSort(undefined);
    }
  }, []);

  useEffect(() => {
    setUrlError(null);

    const timeout = setTimeout(() => {
      if (url.length < 3) {
        return;
      }

      if (!validateUrl(url)) {
        setUrlError("Please enter a valid URL");
        return;
      }

      setPagination(DEFAULT_PAGINATION_STATE);
      setDebouncedUrl(url);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [url]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSort(sort);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [sort]);

  const urlObject = getUrl(debouncedUrl);

  const filter: KeywordsByUrlFilter = {
    url: urlObject?.href ?? "",
    countryCode: selectedCountry.split(":")[0],
    languageCode: selectedCountry.split(":")[1],

    rank: filters.rank ?? undefined,
    searchVolume: filters.searchVolume ?? undefined,
    difficulty: filters.keywordDifficulty ?? undefined,
    searchIntent: filters.searchIntent ?? undefined,
    includeKeywords: filters.includeKeywords ?? undefined,
    excludeKeywords: filters.excludeKeywords ?? undefined,
  };

  const [SearchByURLResponse] = useQuery({
    query: SearchByURLQuery,
    variables: {
      filter: filter,
      sort: debouncedSort,
      paginationOptions: {
        page: pagination.pageIndex,
      },
    },
    pause: !urlObject,
  });

  const data =
    debouncedUrl.length >= 3
      ? (SearchByURLResponse.data?.keywordsByUrl.nodes.map((node) => ({
          ...node,
          id: node.keyword,
        })) ?? [])
      : [];
  const suggestions = SearchByURLResponse.data?.keywordUrlSuggestions ?? [];

  useEffect(() => {
    if (searchByUrlSettingsResponse.data?.keywordSettings) {
      const location =
        searchByUrlSettingsResponse.data?.keywordSettings.defaultGoogleLocation;

      if (location) {
        setSelectedCountry(location);
      }
    }
  }, [searchByUrlSettingsResponse.data?.keywordSettings]);

  return (
    <div className={styles.tableContainer}>
      <InsufficientKeywordCreditsModal
        isOpen={insufficientKeywordCreditsModalOpened}
        onClose={insufficientKeywordCreditsModal.close}
      />
      <Table
        disableSelectAll
        SelectedToolbar={KeywordTableToolbar}
        emptyState={
          <EmptyState
            className={styles.emptyState}
            title={
              debouncedUrl.length
                ? "No keywords found for URL"
                : "Enter a URL to search"
            }
            icon={<Asterisk size="large" />}
          />
        }
        loading={SearchByURLResponse.fetching}
        pagination={pagination}
        setPagination={(value) => {
          if (keywordQueryCreditsRemaining <= 0) {
            insufficientKeywordCreditsModal.open();
            return;
          }

          setPagination(value);
        }}
        totalCount={Math.min(
          SearchByURLResponse.data?.keywordsByUrl.totalCount ?? 0,
          50 * 50,
        )}
        resourceName={{ plural: "keywords", singular: "keyword" }}
        onSortingChange={(value) => {
          if (keywordQueryCreditsRemaining <= 0) {
            insufficientKeywordCreditsModal.open();
            return;
          }

          onSortingChange(value);
        }}
        enableSorting
        clearSelectionOnChange={false}
        isFiltered={!!(filterCount || urlObject)}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        data={data ?? []}
        isSelectable
        Header={
          <div className={styles.header}>
            <div className={styles.headerText}>
              <div>
                <div className={styles.titleRow}>
                  <h2 className={styles.title}>Search by URL</h2>
                </div>
                <p className={styles.description}>
                  Choose any domain or URL and we show you all keywords that
                  rank for it
                </p>
              </div>
              <Suggestions
                suggestions={suggestions}
                onSelect={(suggestion) => {
                  setUrl(suggestion);
                  setDebouncedUrl(suggestion);
                }}
              />
            </div>
            <div className={styles.headerInputs}>
              <div>
                <TextInput
                  autofocus
                  leftSection={<Search size="medium" />}
                  rightSection={
                    <div style={{ pointerEvents: "auto", marginRight: "-6px" }}>
                      <CountryLanguageSelect
                        value={selectedCountry}
                        onChange={(value) => {
                          if (keywordQueryCreditsRemaining <= 0) {
                            insufficientKeywordCreditsModal.open();
                            return;
                          }
                          setSelectedCountry(value);
                        }}
                      />
                    </div>
                  }
                  onChange={(event) => {
                    if (keywordQueryCreditsRemaining <= 0) {
                      insufficientKeywordCreditsModal.open();
                      return;
                    }

                    setUrl(event.currentTarget.value);
                  }}
                  placeholder="URL"
                  value={url}
                  className={styles.search}
                />
                {urlError && (
                  <Feedback className={styles.error}>{urlError}</Feedback>
                )}
              </div>
              <KeywordFilterButton
                includeRank
                filterCount={filterCount}
                filters={filters}
                setFilters={setFilters}
                clearFilters={() => {
                  setFilters({
                    searchVolume: null,
                    keywordDifficulty: null,
                    searchIntent: null,
                    includeKeywords: null,
                    excludeKeywords: null,
                    rank: null,
                  });
                }}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}

export default SearchByURL;
